// Planbord scope-selectie: pure helpers voor de scope "deze weekdag, alle
// toekomstige weken" (BulkScope `this-weekday-future`).
//
// Conform CLAUDE.md (§Tijdzone, locale & geld): de database slaat dag-datums op
// als UTC-midnight van de Europe/Amsterdam-kalenderdag; weekdag- en
// datumvergelijkingen worden in Europe/Amsterdam afgeleid (niet uit process.tz),
// week = ma-zo (ISO). Geen datum-library — alleen Intl.DateTimeFormat + Date.

// ─── Intern: Amsterdam-kalenderdag ──────────────────────────────────────────

/**
 * De Europe/Amsterdam-kalenderdatum van een instant als 'yyyy-mm-dd'.
 *
 * 'en-CA' levert het ISO-formaat (yyyy-mm-dd), zodat lexicografische
 * string-vergelijking gelijk is aan chronologische dag-vergelijking.
 */
function amsterdamYmd(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
}

/**
 * ISO-weekdag (1=ma .. 7=zo) van de Europe/Amsterdam-kalenderdag van `d`.
 *
 * 'en-US' met `weekday: 'short'` geeft een vaste Engelse afkorting terug,
 * onafhankelijk van de host-locale; die mappen we op de ISO-index.
 */
export function isoWeekdayAmsterdam(d: Date): number {
  const short = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Amsterdam',
    weekday: 'short',
  }).format(d)
  const idx: Record<string, number> = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  }
  const wd = idx[short]
  if (wd === undefined) {
    throw new Error(`Onverwachte weekdag-afkorting van Intl: "${short}"`)
  }
  return wd
}

/** NL weekdag-labels per ISO-index (1=ma .. 7=zo). */
const WEEKDAG_LABELS_NL: Record<number, string> = {
  1: 'maandag',
  2: 'dinsdag',
  3: 'woensdag',
  4: 'donderdag',
  5: 'vrijdag',
  6: 'zaterdag',
  7: 'zondag',
}

/**
 * NL weekdag-naam (kleine letter) van de Europe/Amsterdam-kalenderdag van `d`.
 *
 * Gebruikt voor het dynamische dialog-label "Elke {weekdag} vanaf deze week".
 * Bewust kleine letters zodat het midden in een zin past; capitaliseer aan de
 * UI-rand indien nodig.
 */
export function weekdayLabelNL(d: Date): string {
  return WEEKDAG_LABELS_NL[isoWeekdayAmsterdam(d)] ?? ''
}

/**
 * Predikaat voor BulkScope `this-weekday-future`.
 *
 * `entryDate` valt binnen scope als die in Europe/Amsterdam:
 *   1. dezelfde ISO-weekdag heeft als `anchorDate` (bv. beide vrijdag), én
 *   2. op of ná de anchor-kalenderdag valt (`entryDate >= anchorDate`,
 *      inclusief de anchor-dag zelf), én
 *   3. op of vóór `eindDatum` valt indien gezet (`entryDate <= eindDatum`,
 *      inclusief); `null` = onbegrensd vooruit.
 *
 * Alle vergelijkingen op kalenderdag-niveau in Europe/Amsterdam — kloktijd en
 * UTC-offset spelen geen rol. Verleden en andere weekdagen vallen buiten scope.
 */
export function isWeekdayFutureMatch(
  entryDate: Date,
  anchorDate: Date,
  eindDatum: Date | null,
): boolean {
  // 1. Zelfde ISO-weekdag (NL-tijd).
  if (isoWeekdayAmsterdam(entryDate) !== isoWeekdayAmsterdam(anchorDate)) {
    return false
  }
  const entryYmd = amsterdamYmd(entryDate)
  // 2. Op of ná de anchor-dag (string-vergelijking == chronologisch voor ISO).
  if (entryYmd < amsterdamYmd(anchorDate)) {
    return false
  }
  // 3. Op of vóór de einddatum, indien gezet.
  if (eindDatum !== null && entryYmd > amsterdamYmd(eindDatum)) {
    return false
  }
  return true
}
